/** Volunteers {@link DataTable} for the management page. */
import * as React from 'react';
import { User as UserIcon } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { VolunteerRow } from './VolunteerRow';
import { VolunteerCard } from './VolunteerCard';
import { VolunteerFiltersContent } from './VolunteerFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderVolunteerDetails } from './renderVolunteerDetails';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { exportVolunteersExcel, getVolunteers } from '@/services/volunteers.service';
import { deleteUser } from '@/services/users.service';
import { cn } from '@/lib/utils';
import {
  DEFAULT_VOLUNTEER_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  type VolunteerFilters,
  type FilterGroupId,
} from './volunteer-configs';
import {
  buildVolunteerExportQueryParams,
  buildVolunteerListQueryParams,
  hasVolunteerActiveFilters,
} from './volunteer-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { Volunteer } from '@/types/volunteer.types';

/** Props for {@link VolunteersDataTable}. */
interface VolunteersDataTableProps {
  volunteers?: Volunteer[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  layoutWidth?: ElementWidth;
  enableFilters?: boolean;
  defaultFilters?: VolunteerFilters;
  showCreateButton?: boolean;
  onCreateVolunteer?: () => void;
  onEditVolunteer?: (id: string) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  isSection?: boolean;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean; canExportCsv?: boolean };
  /** Filter fetched volunteers by role codes (driven by tab selection). */
  enabledRoles?: string[];
  toolbarLeft?: React.ReactNode;
}

/** Volunteers {@link DataTable} for the management page. */
export const VolunteersDataTable: React.FC<VolunteersDataTableProps> = ({
  volunteers: externalVolunteers,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  layoutWidth: layoutWidthProp = 'full',
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditVolunteer,
  enableFilters = true,
  defaultFilters = DEFAULT_VOLUNTEER_FILTERS,
  isSection = false,
  pageActions,
  showCreateButton = false,
  onCreateVolunteer,
  enabledRoles,
  toolbarLeft,
}) => {
  const queryClient = useQueryClient();
  const { elements: interfaceElements } = useInterfaceElements('volunteers');
  const showExport = pageActions?.canExportCsv === true;
  const [isExporting, setIsExporting] = React.useState(false);

  const dt = useDataTable<VolunteerFilters, FilterGroupId>({
    tableName: 'volunteers',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['users', 'volunteer', dt.currentPage, dt.itemsPerPage, dt.filters, enabledRoles],
    queryFn: async () => {
      return await getVolunteers(
        buildVolunteerListQueryParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
          roleCodes: enabledRoles,
        }),
      );
    },
    enabled: !externalVolunteers,
  });

  const data = React.useMemo(
    () => (externalVolunteers ?? (apiResponse?.data || [])) as Volunteer[],
    [externalVolunteers, apiResponse?.data],
  );
  const totalItems = apiResponse?.total || data.length;
  const isEmptyForExport = React.useMemo(
    () => (externalVolunteers ? data.length === 0 : !isLoading && totalItems === 0),
    [externalVolunteers, data.length, isLoading, totalItems],
  );
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const {
    data: expandedVolunteerData,
    isLoading: isLoadingExpandedVolunteer,
    isError: isExpandedVolunteerError,
    error: expandedVolunteerError,
  } = useQuery({
    queryKey: ['users', 'volunteer', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getVolunteer } = await import('@/services/volunteers.service');
      return await getVolunteer(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedVolunteerError,
    error: expandedVolunteerError,
    catalogKey: 'volunteerNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const expandedItem = (expandedVolunteerData ?? null) as Volunteer | null;

  const hasActiveFilters = React.useMemo(() => hasVolunteerActiveFilters(dt.filters), [dt.filters]);

  const clearFilters = React.useCallback(() => {
    dt.setFilters(defaultFilters);
    dt.setCurrentPage(1);
  }, [dt, defaultFilters]);

  const handleExportExcel = React.useCallback(async () => {
    if (externalVolunteers ? data.length === 0 : !isLoading && totalItems === 0) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportVolunteersExcel(buildVolunteerExportQueryParams(dt.filters, enabledRoles));
      toast.success('Export Excel réussi !');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [data.length, dt.filters, enabledRoles, externalVolunteers, isLoading, totalItems]);

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Volontaire supprimé avec succès !');
      dt.setDeleteId(null);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const volunteerToDelete = React.useMemo(
    () => data.find((v) => v.id === dt.deleteId),
    [data, dt.deleteId],
  );

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSubject = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, subjects: id ? [id] : [] }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleAvailability = React.useCallback(
    (day: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        availability: prev.availability?.includes(day)
          ? prev.availability.filter((d) => d !== day)
          : [...(prev.availability || []), day],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEstablishment = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDateRange = React.useCallback(
    (range: VolunteerFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, customDateFrom: from, customDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEngagementStartDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        engagementStartDateFrom: from,
        engagementStartDateTo: to,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEngagementEndDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        engagementEndDateFrom: from,
        engagementEndDateTo: to,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setExitDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, exitDateFrom: from, exitDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setExitReason = React.useCallback(
    (reason: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, exitReason: reason }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSearch = React.useCallback(
    (value: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: value }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEntryDateRange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, entryDateFrom: from, entryDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <VolunteerFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onSetSearch={setSearch}
        onToggleStatus={toggleStatusFilter}
        onSetSubject={setSubject}
        onToggleAvailability={toggleAvailability}
        onSetEstablishment={setEstablishment}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetEntryDateRange={setEntryDateRange}
        onSetEngagementStartDateRange={setEngagementStartDateRange}
        onSetEngagementEndDateRange={setEngagementEndDateRange}
        onSetExitDateRange={setExitDateRange}
        onSetExitReason={setExitReason}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      setSearch,
      toggleStatusFilter,
      setSubject,
      toggleAvailability,
      setEstablishment,
      setDateRange,
      setCustomDateRange,
      setEntryDateRange,
      setEngagementStartDateRange,
      setEngagementEndDateRange,
      setExitDateRange,
      setExitReason,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="Volontaires"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={13}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                dt.setShowFilters((prev) => !prev);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des volontaires en Excel'
              }
            />
          )}
          {showCreateButton && onCreateVolunteer && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un volontaire"
              onClick={() => {
                onCloseDetails?.();
                onCreateVolunteer();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={clearFilters}
            resultCount={totalItems}
            resultLabel="volontaire(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Contact</th>
          <th>Rôles</th>
          <th>Matières</th>
          <th>Date d'entrée</th>
          <th>Ancienneté</th>
          <th>Heures réalisées</th>
          <th>Heures à réaliser</th>
          <th>Date de désactivation</th>
          <th>Taux de présence</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(volunteer) => (
        <VolunteerRow
          volunteer={volunteer}
          interfaceElements={interfaceElements}
          onView={onToggleRow ? () => onToggleRow(volunteer.id) : undefined}
          onEdit={onEditVolunteer}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(volunteer) => (
        <DetailsViewContent>{renderVolunteerDetails(volunteer)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(volunteer) => (
        <VolunteerCard
          volunteer={volunteer}
          interfaceElements={interfaceElements}
          onView={onToggleRow ? () => onToggleRow(volunteer.id) : undefined}
          onEdit={onEditVolunteer}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={UserIcon}
      emptyText={
        dt.filters.search?.trim()
          ? 'Aucun profil ne correspond à votre recherche'
          : 'Aucun VSC trouvé'
      }
      title="VSCs"
      sidebarMenuId="menu-volunteers"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedVolunteer}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(volunteer) => (
        <DetailsViewContent>{renderVolunteerDetails(volunteer)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={
            volunteerToDelete ? `${volunteerToDelete.firstName} ${volunteerToDelete.lastName}` : ''
          }
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
